import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";

import { platformAdapter } from "./adapter";
import { createUploadApi, getUploadAccessToken } from "./api";
import { UPLOAD_V2_API_URL, isUploadV2Enabled } from "./config";
import { createUploadManager, idleUploadState } from "./manager";
import { createResumeStore } from "./resumeStore";

// v2 is used only when the platform has an adapter (web, Android 4.3.6+) AND
// the build flag allows it there. Otherwise everything stays on the current
// upload path and this module does nothing.
export const isUploadV2Available = () =>
  Boolean(platformAdapter) && isUploadV2Enabled(platformAdapter.platform);

let manager = null;

// The app-wide manager (created on first use), so uploads outlive screens.
export function getUploadManager() {
  if (!isUploadV2Available()) return null;
  if (!manager) {
    manager = createUploadManager({
      api: createUploadApi({
        baseUrl: UPLOAD_V2_API_URL,
        getAccessToken: getUploadAccessToken,
      }),
      adapter: platformAdapter,
      store: createResumeStore({
        getStorage: () => platformAdapter.getStorage?.(),
      }),
      log: (...args) => console.log("[uploadV2]", ...args),
    });
  }
  return manager;
}

// The upload state for one event plus actions. While mounted it keeps the
// status polling going for a pending conversion and, given `event`, watches
// the eventUpdated data for the finished conversion.
export function useUploadV2(slug, { event } = {}) {
  const uploads = getUploadManager();
  const active = Boolean(uploads && slug);

  const subscribe = useCallback(
    (onChange) => (active ? uploads.subscribe(slug, onChange) : () => {}),
    [uploads, slug, active]
  );
  const getSnapshot = useCallback(
    () => (active ? uploads.getState(slug) : idleUploadState),
    [uploads, slug, active]
  );
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(
    () => (active ? uploads.retain(slug) : undefined),
    [uploads, slug, active]
  );

  useEffect(() => {
    if (active && event) uploads.observeEvent(slug, event);
  }, [uploads, slug, active, event]);

  const actions = useMemo(
    () => ({
      getState: getSnapshot,
      start: (options) =>
        active ? uploads.start(slug, options) : Promise.resolve(null),
      pause: () => (active ? uploads.pause(slug) : false),
      resume: (options) =>
        active ? uploads.resume(slug, options) : Promise.resolve(null),
      cancel: () => (active ? uploads.cancel(slug) : false),
      dismiss: () => (active ? uploads.dismiss(slug) : false),
      markSeen: () => (active ? uploads.markSeen(slug) : false),
      resumeWithFile: (file, options) =>
        active ? uploads.resumeWithFile(slug, file, options) : { error: null },
      uploadAgain: () =>
        active ? uploads.uploadAgain(slug) : Promise.resolve(null),
      retryConversion: () =>
        active ? uploads.retryConversion(slug) : Promise.resolve(null),
    }),
    [uploads, slug, active, getSnapshot]
  );

  return { available: Boolean(uploads), state, ...actions };
}
